import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingBlock } from '../components/Spinner';
import { useIsAdmin } from '../lib/auth';
import { clearSwrCache, swrLoad } from '../lib/swrCache';
import SectionTabs from '../components/SectionTabs';
import { SERIES_TABS } from '../lib/section-tabs';
import type { EventSeries, SeriesType, Venue } from '../types';
import DistanceControl from '../components/DistanceControl';
import { useStoredOrigin } from '../lib/useStoredOrigin';
import { distanceMiles } from '../lib/eventFilters';
import SeriesEditModal from '../components/SeriesEditModal';
import FavoriteButton from '../components/FavoriteButton';
import { useFavorites } from '../lib/favorites';
import { useIsAuthenticated } from '@azure/msal-react';

const TYPE_LABELS: Record<SeriesType, string> = {
  'recurring-event': 'Recurring event',
  'aggregator': 'Aggregator',
  'organizer': 'Organizer',
};

type SeriesSortKey = 'name-asc' | 'distance-asc';

export default function SeriesPublicPage() {
  const isAdmin = useIsAdmin();
  const [series, setSeries] = useState<EventSeries[] | null>(null);
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | SeriesType>('all');
  const [sort, setSort] = useState<SeriesSortKey>('name-asc');
  const { origin, maxDistanceMiles, setOriginState } = useStoredOrigin();
  const [editingSeries, setEditingSeries] = useState<EventSeries | null>(null);
  const favorites = useFavorites();
  const isAuthed = useIsAuthenticated();
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void swrLoad<EventSeries[]>({
      key: 'series:public',
      fetcher: () => api.get<EventSeries[]>('/api/series/public'),
      onData: (data) => setSeries(data),
      onError: () => setSeries([]),
      isCancelled: () => cancelled,
    });
    // Series have no coordinates of their own — we resolve distance via the
    // optional parentVenueId. Venues table is small (~hundreds) so a single
    // fetch is fine.
    void swrLoad<Venue[]>({
      key: 'venues:public',
      fetcher: () => api.get<Venue[]>('/api/venues/public'),
      onData: (data) => setVenues(data),
      onError: () => setVenues([]),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, []);

  const venuesById = useMemo(() => {
    const map = new Map<string, Venue>();
    for (const v of venues ?? []) map.set(v.rowKey, v);
    return map;
  }, [venues]);

  const distanceForSeries = (s: EventSeries): number => {
    if (!origin || !s.parentVenueId) return Infinity;
    const v = venuesById.get(s.parentVenueId);
    if (!v) return Infinity;
    return distanceMiles(origin.lat, origin.lng, v.lat, v.lng);
  };

  const visible = useMemo<EventSeries[]>(() => {
    if (!series) return [];
    let list = typeFilter === 'all' ? series : series.filter((s) => s.partitionKey === typeFilter);
    if (origin && typeof maxDistanceMiles === 'number' && maxDistanceMiles > 0) {
      list = list.filter((s) => distanceForSeries(s) <= maxDistanceMiles);
    }
    list = [...list];
    if (favoritesOnly) list = list.filter((s) => favorites.seriesIds.has(s.rowKey) || (s.parentVenueId && favorites.venueIds.has(s.parentVenueId)));
    if (sort === 'distance-asc' && origin) {
      list.sort((a, b) => distanceForSeries(a) - distanceForSeries(b));
    } else {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, venuesById, typeFilter, origin, maxDistanceMiles, sort, favoritesOnly, favorites]);

  // Drop a "distance-asc" sort that has no origin to sort by.
  useEffect(() => {
    if (!origin && sort === 'distance-asc') setSort('name-asc');
  }, [origin, sort]);

  const distanceLabel = (s: EventSeries): string | null => {
    if (!origin) return null;
    const d = distanceForSeries(s);
    if (!Number.isFinite(d)) return null;
    const yards = Math.round(d * 1760);
    return `${yards.toLocaleString()} yd away`;
  };

  return (
    <SectionTabs ariaLabel="Series sections" items={SERIES_TABS}>
      <h1>Event series</h1>
      <p className="lead">
        Recurring events (Puzzled Pint, Stitch &amp; Bitch), aggregators (Riverside Arts Market), and organizers
        that bring events to multiple venues across the area.
      </p>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <label htmlFor="series-type" className="form-label visually-hidden">Filter by type</label>
        <select
          id="series-type"
          className="form-select form-select-sm w-auto"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as 'all' | SeriesType)}
        >
          <option value="all">All types</option>
          <option value="recurring-event">Recurring events</option>
          <option value="aggregator">Aggregators</option>
          <option value="organizer">Organizers</option>
        </select>
        <DistanceControl
          origin={origin}
          maxDistanceMiles={maxDistanceMiles}
          onChange={setOriginState}
          idPrefix="mh-series-distance"
        />
        <label htmlFor="series-sort" className="visually-hidden">Sort by</label>
        <select
          id="series-sort"
          className="form-select form-select-sm w-auto"
          value={sort}
          onChange={(e) => setSort(e.target.value as SeriesSortKey)}
        >
          <option value="name-asc">Name A–Z</option>
          <option value="distance-asc" disabled={!origin}>
            Nearest{origin ? '' : ' (set address)'}
          </option>
        </select>
        {isAuthed && (
          <button
            type="button"
            className={`btn btn-sm ${favoritesOnly ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setFavoritesOnly((v) => !v)}
            aria-pressed={favoritesOnly}
            title="Show only favorited series (and series at favorited venues)"
          >
            <span aria-hidden="true">★</span> Favorites only
          </button>
        )}
        {series && (
          <span className="text-muted small ms-auto" aria-live="polite">
            Showing {visible.length} of {series.length} series
          </span>
        )}
      </div>

      {origin && (
        <p className="text-muted small mb-3">
          Distance is measured from the series’ home venue when one is known. Series without a parent venue are
          excluded from distance filters and sink to the bottom when sorted by distance.
        </p>
      )}

      {series === null && <LoadingBlock label="Loading series…" />}
      {series !== null && visible.length === 0 && (
        <div className="alert alert-info">No series match the current filter.</div>
      )}

      <div className="row g-3">
        {visible.map((s) => {
          const dist = distanceLabel(s);
          return (
            <div key={s.rowKey} className="col-md-6 col-lg-4">
              <article className={`card h-100${favorites.seriesIds.has(s.rowKey) ? ' mh-favorite' : (s.parentVenueId && favorites.venueIds.has(s.parentVenueId)) ? ' mh-favorite-inherited' : ''}`}>
                <div className="card-body">
                  <h2 className="h5 card-title d-flex align-items-start gap-2">
                    <Link to={`/series/${s.rowKey}`} className="flex-grow-1">{s.name}</Link>
                    <FavoriteButton kind="series" targetId={s.rowKey} targetName={s.name} />
                    {isAdmin && (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-muted mh-event-row__edit"
                        onClick={() => setEditingSeries(s)}
                        aria-label={`Edit ${s.name}`}
                        title="Edit series"
                      >
                        <i className="bi bi-pencil-square" aria-hidden="true" />
                      </button>
                    )}
                  </h2>
                  <p className="small text-muted mb-2">
                    {TYPE_LABELS[s.partitionKey]}
                    {dist && <> &middot; <span className="text-body">{dist}</span></>}
                  </p>
                  {s.description && <p className="small mb-2">{s.description}</p>}
                  <p className="small mb-2">
                    {s.website && <a href={s.website} target="_blank" rel="noreferrer noopener">Website</a>}
                    {s.instagramHandle && (
                      <> &middot; <a href={`https://instagram.com/${s.instagramHandle}`} target="_blank" rel="noreferrer noopener">Instagram</a></>
                    )}
                    {s.facebookUrl && (
                      <> &middot; <a href={s.facebookUrl} target="_blank" rel="noreferrer noopener">Facebook</a></>
                    )}
                  </p>
                  <p className="small mb-0">
                    <Link to={`/series/${s.rowKey}`}>View upcoming events →</Link>
                  </p>
                </div>
              </article>
            </div>
          );
        })}
      </div>
      <SeriesEditModal
        series={editingSeries}
        onClose={() => setEditingSeries(null)}
        onSaved={(saved) => {
          setSeries((prev) => (prev ? prev.map((s) => (s.rowKey === saved.rowKey ? saved : s)) : prev));
          clearSwrCache('series:public');
          setEditingSeries(null);
        }}
      />
    </SectionTabs>
  );
}
